import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import { TextField, MenuItem, IconButton, Tooltip, Typography } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

import { Context as CurrenciesContext } from '../../../api/currencies/context';
import { Context as ClientsContext } from '../../../api/clients/context';
import { Client } from '../../../api/clients/types';
import useExchangeRates from '../../../routes/Setup/Finance/Currencies/useExchangeRates';
import {
    EXCHANGE_RATE_DECIMAL_VIEW_NUMBER,
    getToFixedNumber,
    taytanFormatNumberToString
} from '../../../utils/mathUtils';

import headerCurrencyConverterStyles from './headerCurrencyConverterStyles';

const inputStyle = { backgroundColor: 'transparent', color: '#fff' } as const;

const HeaderCurrencyConverter = () => {
    const classes = headerCurrencyConverterStyles();
    const currencies = useContext(CurrenciesContext);
    const [isOpen, setIsOpen] = useState(() => {
        // Start closed below desktop sidebar breakpoint so hamburger menu is visible
        if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1279px)').matches) {
            return false;
        }
        return true;
    });

    const currencyOptions = useMemo(
        () =>
            (currencies.dataAsArray || []).map((c: any) => ({
                _id: c._id,
                code: c.code,
                symbol: c.symbol
            })),
        [currencies.dataAsArray]
    );

    const { getExchangeRate } = useExchangeRates({});

    const clientsContext = useContext(ClientsContext);
    const currencySettings = (clientsContext.selected as Client)?.currencySettings;

    const defaultFrom = useMemo(() => {
        const primaryId =
            typeof currencySettings?.primaryCurrency === 'object'
                ? (currencySettings?.primaryCurrency as any)?._id
                : currencySettings?.primaryCurrency;
        return primaryId || currencyOptions.find((c: any) => c.code === 'USD')?._id || '';
    }, [currencySettings, currencyOptions]);

    const defaultTo = useMemo(() => {
        const localId =
            typeof currencySettings?.localCurrency === 'object'
                ? (currencySettings?.localCurrency as any)?._id
                : currencySettings?.localCurrency;
        return localId || currencyOptions.find((c: any) => c.code === 'ILS')?._id || '';
    }, [currencySettings, currencyOptions]);

    const [fromCurrency, setFromCurrency] = useState('');
    const [toCurrency, setToCurrency] = useState('');
    const [amount, setAmount] = useState('1');

    React.useEffect(() => {
        if (currencyOptions.length > 0 && !fromCurrency) {
            setFromCurrency(defaultFrom);
            setToCurrency(defaultTo);
        }
    }, [currencyOptions, defaultFrom, defaultTo]);

    // Auto-calculate result on demand
    const result = useMemo(() => {
        if (!amount || !fromCurrency || !toCurrency) return '';
        if (fromCurrency === toCurrency) return amount;
        const rate = getExchangeRate(fromCurrency, toCurrency);
        if (rate === 'N/A') return 'N/A';
        return getToFixedNumber(+amount * rate, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER).toFixed(4);
    }, [amount, fromCurrency, toCurrency, getExchangeRate]);

    const handleSwap = useCallback(() => {
        setFromCurrency(toCurrency);
        setToCurrency(fromCurrency);
    }, [fromCurrency, toCurrency]);

    // Exchange rate display based on client's primary ↔ local currency
    const exchangeRateLabel = useMemo(() => {
        if (!currencySettings?.primaryCurrency || !currencySettings?.localCurrency) return '';
        const primaryId =
            typeof currencySettings.primaryCurrency === 'object'
                ? (currencySettings.primaryCurrency as any)?._id
                : currencySettings.primaryCurrency;
        const localId =
            typeof currencySettings.localCurrency === 'object'
                ? (currencySettings.localCurrency as any)?._id
                : currencySettings.localCurrency;
        if (!primaryId || !localId || primaryId === localId) return '';
        const primary = currencyOptions.find((c: any) => c._id === primaryId);
        const local = currencyOptions.find((c: any) => c._id === localId);
        if (!primary || !local) return '';
        const rate = getExchangeRate(primaryId, localId);
        if (rate === 'N/A' || !rate) return '';
        return `${primary.symbol} = ${taytanFormatNumberToString(rate, EXCHANGE_RATE_DECIMAL_VIEW_NUMBER)} ${local.symbol}`;
    }, [clientsContext.selected, currencyOptions, getExchangeRate]);

    if (currencyOptions.length === 0) return null;

    return (
        <div className={classes.root}>
            {/* Exchange rate label hidden — already shown in converter */}
            {/* Toggle icon (right side due to RTL on root) */}
            <Tooltip title="Currency Converter">
                <IconButton
                    size="small"
                    onClick={() => setIsOpen((prev) => !prev)}
                    className={classes.toggleBtn}
                >
                    <CompareArrowsIcon className={classes.toggleIcon} />
                </IconButton>
            </Tooltip>

            {/* Collapsible converter panel (expands to the left) */}
            <div className={`${classes.panel} ${isOpen ? classes.panelOpen : classes.panelClosed}`}>
                <TextField
                    select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value)}
                    variant="outlined"
                    size="small"
                    className={`${classes.converterField} ${classes.currencySelect}`}
                    InputProps={{ style: inputStyle }}
                    SelectProps={{
                        SelectDisplayProps: { style: inputStyle },
                        MenuProps: {
                            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                            transformOrigin: { vertical: 'top', horizontal: 'center' }
                        }
                    }}
                >
                    {currencyOptions.map((c: any) => (
                        <MenuItem key={c._id} value={c._id}>
                            {c.code}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Amount"
                    className={`${classes.converterField} ${classes.amountInput}`}
                    InputProps={{ style: inputStyle }}
                    inputProps={{ style: inputStyle }}
                />

                <IconButton size="small" onClick={handleSwap} className={classes.swapBtn}>
                    <SwapHorizIcon className={classes.swapIcon} />
                </IconButton>

                <TextField
                    select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value)}
                    variant="outlined"
                    size="small"
                    className={`${classes.converterField} ${classes.currencySelect}`}
                    InputProps={{ style: inputStyle }}
                    SelectProps={{
                        SelectDisplayProps: { style: inputStyle },
                        MenuProps: {
                            anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
                            transformOrigin: { vertical: 'top', horizontal: 'center' }
                        }
                    }}
                >
                    {currencyOptions.map((c: any) => (
                        <MenuItem key={c._id} value={c._id}>
                            {c.code}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    value={result}
                    variant="outlined"
                    size="small"
                    placeholder="—"
                    className={`${classes.converterField} ${classes.resultInput}`}
                    InputProps={{ readOnly: true, style: inputStyle }}
                    inputProps={{ style: inputStyle }}
                />

            </div>
        </div>
    );
};

export default memo(HeaderCurrencyConverter);
